"use client";

import { usePathname } from "next/navigation";
import { IsProduction } from "@/config/frontend";
import { buildBreadcrumbSchema, buildBreadcrumbTrail } from "@/lib/seo/breadcrumbs";

/**
 * Emits BreadcrumbList markup for every static marketing page from one place,
 * looking the current path up in the label map in lib/seo/breadcrumbs.ts.
 *
 * A client component only because layouts have no server-side access to the
 * pathname: reading it via headers() would opt the whole marketing tree out of
 * static rendering, which is the opposite of what we want. This still lands in
 * the prerendered HTML — client components are server-rendered at build time —
 * so Google sees the markup without executing any JavaScript.
 *
 * Pages whose crumb label comes from the backend (blog posts, eSIM
 * destinations) are not in the label map and render nothing here; they mount
 * BreadcrumbJsonLd themselves with the real title.
 */
export function BreadcrumbJsonLdAuto() {
  const pathname = usePathname();

  if (!IsProduction || !pathname) return null;

  // Trailing slashes and query strings never reach usePathname, but a stray one
  // would silently miss every label; normalise rather than trust that.
  const path = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";

  const trail = buildBreadcrumbTrail({ path });
  if (!trail) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBreadcrumbSchema(trail)) }}
    />
  );
}

export default BreadcrumbJsonLdAuto;
